/**
 * The ProGST API client.
 *
 * It is a thin layer over `session.authed` from `@fiveox/shell` — that is
 * where the bearer, the single serialized refresh and the one place a session
 * ends already live (see `session.ts`). What this file adds is the two things
 * the shell cannot know about: **the org header** and **this app's error
 * envelope**.
 */

import { SERVICE_API } from './config'
import { SELECTED_ORG_KEY, setSelectedOrgId } from './orgs'
import { session } from './session'

/**
 * A refusal with the machine code the service sent.
 *
 * The code is the contract: a screen switches on `SEAT_LIMIT_EXCEEDED` to say
 * "5 of 5 seats in use" and on `NOT_ENTITLED` to render the paywall. Throwing a
 * bare `Error` here is what produces "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the refusal is about entitlement rather than about this user. */
  get isPaywall(): boolean {
    return this.code === 'NOT_ENTITLED' || this.code === 'USAGE_LIMIT_EXCEEDED'
  }

  get entitlementReason(): string | undefined {
    const reason = this.details.entitlement_reason
    return typeof reason === 'string' ? reason : undefined
  }
}

// -- the selected org --------------------------------------------------------
//
// A user belongs to many orgs and switches between them, so the selected org
// is **not** derivable from the token: the client sends it and the service
// proves the user may act in it (FiveOX ADR-0029).

let currentOrgId: string | null =
  typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_ORG_KEY)

const orgListeners = new Set<() => void>()

export function setOrgId(orgId: string | null): void {
  if (orgId === currentOrgId) return
  currentOrgId = orgId
  setSelectedOrgId(orgId)
  for (const listener of orgListeners) listener()
}

export const getOrgId = () => currentOrgId

export function subscribeOrg(listener: () => void): () => void {
  orgListeners.add(listener)
  return () => orgListeners.delete(listener)
}

// -- the fetcher -------------------------------------------------------------

/**
 * **`X-Fiveox-Org`** — the header the whole estate speaks. Not `X-Org-Id`:
 * gst-service invented that name and no core service reads it, so a core
 * caller sending the correct header would have been refused. Corrected
 * 2026-08-25.
 *
 * **The new name only** (ADR-0025). The estate renamed `X-Fiveox-Org` by
 * dual-accept; gst-service reads both until the platform's Wave 6, so a stale
 * bundle keeps working, but this header goes to our service and nowhere else,
 * so there is no reader here that needs the old name sent.
 */
const ORG_HEADER = 'X-Fiveox-Org'

export interface Envelope<T> {
  success: boolean
  data: T
  meta?: Record<string, unknown>
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Multipart upload — sent as-is; the browser writes the boundary header. */
  form?: FormData
  /** For routes that are not org-scoped. */
  withoutOrg?: boolean
  /** A step-up assertion from core-mfa, for an action that cannot be undone. */
  stepUpAssertion?: string
}

export async function api<T>(path: string, options: Options = {}): Promise<Envelope<T>> {
  // **A plain object, not a `Headers` instance — and this is not a style
  // choice.** `@fiveox/shell`'s `authed` merges with
  // `{ ...(init.headers ?? {}), authorization: ... }`, and spreading a
  // `Headers` yields `{}` because it has no enumerable own properties. Passing
  // one silently drops every header except the bearer the shell adds itself.
  //
  // That cost an afternoon: the org header never left the browser, the
  // service refused with 400, the UI read it as `authority_unreachable` and
  // told the user the permission service was down. `content-type` was being
  // lost the same way, so every POST would have followed.
  //
  // Reported upstream — see docs/PLATFORM_INTEGRATION.md §9.3.
  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(options.headers).entries()) headers[key] = value
  // JSON only when it IS json — a FormData body must not carry a forced
  // content-type, or the multipart boundary never gets written.
  if (options.body !== undefined && !options.form) headers['content-type'] = 'application/json'
  if (!options.withoutOrg) {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
  }
  if (options.stepUpAssertion) headers['X-StepUp-Assertion'] = options.stepUpAssertion

  // `session.authed` owns the bearer, the 401 refresh-and-replay, and ending
  // the session when the refresh comes back spent.
  const res = await session.authed(`${SERVICE_API}${path}`, {
    ...options,
    headers,
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  })

  const body = (await res.json().catch(() => null)) as
    | (Envelope<T> & {
        error?: { code: string; message: string; details?: Record<string, unknown> }
      })
    | null

  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'Something went wrong.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body
}

// -- the shapes the platform half serves -------------------------------------

/**
 * What the GSTIN itself says — offline, deterministic, no credential.
 *
 * Note what is NOT here: `registration_type` and `registration_status`. Neither
 * is encoded in a GSTIN, and registration type gates FR-3, so a field for it on
 * this object would invite exactly the wrong fill.
 */
export interface DerivedGstinFacts {
  state_code: string
  pan: string
  /** The PAN's fourth character in words. `null` for a letter outside the
   *  allocation — the honest answer to a character we do not recognise. */
  entity_type: string | null
  constitution: string | null
  /** This PAN holds more than one registration in this state (character 13). */
  is_additional_registration: boolean
  /** Character 14 is `Z`. Anything else is a different category of
   *  registration, and worth saying rather than assuming away. */
  is_ordinary_taxpayer: boolean
}

/**
 * The public register's answer, in OUR vocabulary — the register's own
 * abbreviations (`lgnm`, `ctb`, `dty`) stop at the service's adapter.
 *
 * Everything past the GSTIN is nullable because a register answer routinely
 * is: a field this omits must stay blank rather than acquire a default, or the
 * record will assert something nobody said.
 */
export interface RegisterProfile {
  legal_name: string | null
  trade_name: string | null
  /** Active / Cancelled / Suspended. Reported, never used to refuse: a
   *  practice onboards a cancelled registration to file its final return. */
  registration_status: string | null
  constitution: string | null
  registration_type: string | null
  registered_on: string | null
  address_line: string | null
  city: string | null
  pincode: string | null
  state_code: string | null
}

/**
 * Two sources, kept apart on purpose. "Proprietorship, read from your GSTIN"
 * and "Proprietorship, per the register" are different claims, and a screen
 * handed one merged object will present the first as the second.
 */
export interface TaxpayerLookup {
  gstin: string
  derived: DerivedGstinFacts
  /** `null` while ADR-0004's gate is shut — `register_gate` then says why. */
  register: RegisterProfile | null
  register_gate: string | null
  /** The same taxpayer, registered elsewhere, already on this firm's list.
   *  Not a conflict: one PAN across several states is ordinary. */
  also_filed_for_this_pan: {
    id: string
    legal_name: string
    gstin: string
    state_code: string
  }[]
}

export interface ClientRecord {
  id: string
  legal_name: string
  trade_name: string | null
  gstin: string
  state_code: string
  registration_type: string
  status: 'active' | 'archived'
  engagement_owner_user_id: string | null
  created_at: string
  /**
   * Present only on a single read, and only for a caller holding
   * `gst.client.update`. A list response is cached and proxied far more widely
   * than one client's own screen, and these are personal data.
   */
  pan?: string | null
  address_line?: string | null
  city?: string | null
  pincode?: string | null
  contact_person?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  signatory_name?: string | null
  signatory_designation?: string | null
  constitution?: string | null
}

/** The editable subset — mirrors the service's `ClientPatch`, nothing wider. */
export interface ClientUpdate {
  legal_name?: string
  trade_name?: string | null
  registration_type?: string
  address_line?: string | null
  city?: string | null
  pincode?: string | null
  contact_person?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  signatory_name?: string | null
  signatory_designation?: string | null
  constitution?: string | null
}

export interface ActivityEvent {
  id: string
  at: string
  action: string
  subject_type: string
  subject_id: string | null
  actor_user_id: string | null
  actor_kind: string
  /**
   * As recorded. Carries content keys, reason codes and the reviewer's own
   * words — which is the point, since "why did this row leave the return" is
   * answered by the note and by nothing else. Never a filename.
   */
  detail: Record<string, unknown>
}

export const clients = {
  list: (params: { search?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ClientRecord[]>(`/api/v1/clients${suffix}`)
  },
  get: (id: string) => api<ClientRecord>(`/api/v1/clients/${id}`),
  /**
   * What people did to this client's work, newest first.
   *
   * Reads the audit trail; records nothing. Keyset paging on `at` rather than
   * an offset, because an audit trail grows at the head and an offset page two
   * shifts under the reader every time something happens.
   */
  activity: (id: string, params: { limit?: number; before?: string; action?: string } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<{ events: ActivityEvent[]; next_before: string | null }>(
      `/api/v1/clients/${id}/activity${suffix}`,
    )
  },
  /**
   * Only the fields the service's `ClientPatch` accepts. GSTIN, PAN, state and
   * status are deliberately not among them: the GSTIN is the record's identity
   * under `uq_gst_clients_org_gstin_active` (ADR-0002), and status moves
   * through `/archive` so the count that meters the plan stays correct.
   */
  update: (id: string, body: ClientUpdate) =>
    api<ClientRecord>(`/api/v1/clients/${id}`, { method: 'PATCH', body }),
  create: (body: {
    legal_name: string
    gstin: string
    trade_name?: string
    registration_type?: string
    constitution?: string
  }) => api<ClientRecord>('/api/v1/clients', { method: 'POST', body }),
  /**
   * What the public register says about a GSTIN (FR-C13).
   *
   * Reads; creates nothing. `POST /clients` is still the only thing that makes
   * a record, and every field this fills stays editable — the register
   * suggests, the firm decides who they act for.
   *
   * **It answers with the gate shut.** Only the `register` half waits on
   * ADR-0004; reading a GSTIN needs nobody's permission, so this is a 200 with
   * `register: null` rather than a refusal. Whether to offer the control comes
   * from `meta.taxpayer_lookup.available` on the client list — the screen is
   * TOLD, it does not work it out.
   */
  lookup: (gstin: string) =>
    api<TaxpayerLookup>('/api/v1/clients/lookup', { method: 'POST', body: { gstin } }),
  archive: (id: string) =>
    api<ClientRecord>(`/api/v1/clients/${id}/archive`, { method: 'POST' }),
}

/**
 * The PAN-level grain (ADR-0008). A group aggregates a PAN's registrations, and
 * carries the previous-FY turnover that decides how many HSN digits this
 * taxpayer must report — the figure `hsn.digits` reads and, until now, nothing
 * could set.
 */
export interface TaxpayerGroupRecord {
  id: string
  pan: string
  display_name: string
  previous_fy_turnover_paise: number | null
  turnover_fy: string | null
  member_count?: number
}

/**
 * Three answers kept apart on purpose. `confirmed` — a person joined this
 * client to the group, so its turnover is this client's. `suggested` — the PAN
 * matches a group nobody confirmed, so the figure is NOT yet this client's.
 * `null` — no group for this PAN. The screen renders the difference; it never
 * collapses it into "has a turnover".
 */
export interface GroupForClient {
  state: 'confirmed' | 'suggested' | null
  group: TaxpayerGroupRecord | null
  suggested_from_pan: boolean
}

export const groups = {
  list: () => api<{ groups: TaxpayerGroupRecord[] }>('/api/v1/groups'),
  forClient: (clientId: string) =>
    api<GroupForClient>(`/api/v1/groups/for-client/${clientId}`),
  create: (body: { pan: string; display_name: string }) =>
    api<{ group: TaxpayerGroupRecord }>('/api/v1/groups', { method: 'POST', body }),
  /** The D-16 confirmation write: a PAN match proposes, a person agrees. */
  addMember: (groupId: string, body: { client_id: string; suggested_from_pan: boolean }) =>
    api<{ group: TaxpayerGroupRecord }>(`/api/v1/groups/${groupId}/members`, {
      method: 'POST',
      body,
    }),
  /** Both fields together, or both null to clear and send the rule back to holding. */
  setTurnover: (
    groupId: string,
    body: { previous_fy_turnover_paise: number | null; turnover_fy: string | null },
  ) =>
    api<{ group: TaxpayerGroupRecord }>(`/api/v1/groups/${groupId}/turnover`, {
      method: 'PATCH',
      body,
    }),
}

// -- the domain half's shapes (W1-16 routes; the seam, filling in) -----------

/**
 * FR-15 on the wire: exactly one of `stage` / `held` is present, and a held
 * row carries its `next_action` sentence. The screen renders these; it never
 * re-decides them (ADR-0014).
 */
export interface DocumentRecord {
  id: string
  client_id: string
  source_filename: string | null
  relative_path: string | null
  source_lane: string | null
  period: string | null
  doc_type: string | null
  status: string
  checksum_ok: boolean | null
  confidence: number | null
  producer: string
  producer_version: string
  parent_id: string | null
  stage?: string
  held?: string
  next_action?: string | null
  /** What a reviewer may do about this hold. Served by the service so the
   *  hold vocabulary lives in one place; empty means nothing to click. */
  dispositions?: string[]
  normalized?: Record<string, unknown> | null
  size_bytes: number | null
  at: string | null
  source?: DocumentSource
  /** Null until a person has asserted this document is right. */
  verified?: { at: string; by: string | null; remark: string | null } | null
}

export interface IntakeSuggestion {
  client_name: string | null
  period: string | null
  lane: string | null
}

/**
 * Mapping profiles — the second extraction path (ADR-0026).
 *
 * Every one of these is offered ONLY for a document the pinned engine could
 * not read. The service enforces that; this file does not decide it, and must
 * not start deciding it, because then the rule would live in two places.
 */
export interface SheetGrid {
  sheet: string
  header_row: number | null
  /** field → `name:<header>` or `col:<index>`, what we think the columns are. */
  suggested: Record<string, string>
  /** The raw grid, so a person binds what they can SEE, not what we guessed. */
  rows: string[][]
}

export interface InspectBody {
  document_id: string
  /** `purchase` reads the grid with the supplier-side vocabulary. */
  direction: string | null
  /** What a profile saved from this dialog stamps. Sent back on save. */
  doc_type: string
  /** The fields a profile may bind — served, never duplicated here. */
  bindable: string[]
  sheets: SheetGrid[]
}

export interface MappingProfileRecord {
  id: string
  name: string
  sheet: string
  header_row: number
  columns: Record<string, string>
  doc_type: string
  /** Null until it has parsed something. An unused profile is a signal. */
  last_used_at: string | null
}

export const profiles = {
  /** The grid and our guess at it. Reads; decides nothing. */
  inspect: (documentId: string) =>
    api<InspectBody>(`/api/v1/documents/${documentId}/inspect`),
  list: (clientId: string) =>
    api<{ profiles: MappingProfileRecord[] }>(
      `/api/v1/mapping-profiles?client_id=${clientId}`,
    ),
  save: (
    clientId: string,
    body: {
      name: string
      sheet: string
      header_row: number
      columns: Record<string, string>
      doc_type?: string
    },
  ) =>
    api<MappingProfileRecord>('/api/v1/mapping-profiles', {
      method: 'POST',
      body: { ...body, client_id: clientId },
    }),
  /**
   * Read a held document through a saved binding.
   *
   * A refusal here is the guard working: the profile did not fit the file and
   * the service declined rather than reading the wrong columns confidently.
   * The message carries the reason, so surface it rather than a generic one.
   */
  apply: (documentId: string, profileId: string) =>
    api<DocumentRecord>(`/api/v1/documents/${documentId}/apply-profile`, {
      method: 'POST',
      body: { profile_id: profileId },
    }),
}

export const documents = {
  list: (params: { client_id?: string; stage?: string; held?: string; period?: string } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<DocumentRecord[]>(`/api/v1/documents${suffix}`)
  },
  get: (id: string) => api<DocumentRecord>(`/api/v1/documents/${id}`),
  dispose: (id: string, body: { disposition: string; note?: string }) =>
    api<DocumentRecord>(`/api/v1/documents/${id}/dispose`, { method: 'POST', body }),
  reclassify: (id: string, body: { doc_type: string }) =>
    api<DocumentRecord>(`/api/v1/documents/${id}/reclassify`, { method: 'POST', body }),
  /** Removes an upload outright. Refused once its rows are booked — dispose then. */
  remove: (id: string) => api<null>(`/api/v1/documents/${id}`, { method: 'DELETE' }),
  /**
   * A freshly-minted read URL for the ORIGINAL upload. A capability: minted per
   * open, never stored, never put in an href on load. POST because a GET could
   * be prefetched, which would mint one nobody asked for and record a view that
   * never happened.
   */
  sourceUrl: (id: string) =>
    api<{ url: string; expires_in: number; sha256: string; render: string }>(
      `/api/v1/documents/${id}/source-url`,
      { method: 'POST' },
    ),
}

export const intake = {
  /** Names only, before any bytes move — a warning, never a refusal. */
  precheck: (filenames: string[], laneDirection: 'sales' | 'purchase') =>
    api<{ mismatches: Array<{ filename: string; looks_like: string; reason: string }> }>(
      '/api/v1/intake/precheck',
      { method: 'POST', body: { filenames, lane_direction: laneDirection } },
    ),
  suggest: (relativePath: string) =>
    api<IntakeSuggestion>(
      `/api/v1/intake/suggest?relative_path=${encodeURIComponent(relativePath)}`,
    ),
  upload: (input: {
    file: File
    client_id: string
    period: string
    relative_path?: string
    lane?: string
  }) => {
    const form = new FormData()
    form.set('file', input.file, input.file.name)
    form.set('client_id', input.client_id)
    form.set('period', input.period)
    if (input.relative_path) form.set('relative_path', input.relative_path)
    if (input.lane) form.set('lane', input.lane)
    return api<DocumentRecord>('/api/v1/intake/files', { method: 'POST', form })
  },
}

/** What the server says can be shown for a document's original upload. */
export interface DocumentSource {
  /** False when the file was never kept — not "lost", never captured. */
  available: boolean
  /** Decided by the SERVICE, never re-derived here from a filename. */
  render: 'pdf' | 'image' | 'none'
  content_type: string
}

export interface ReviewRow {
  content_key: string
  /** field → 'user' | 'rule', for every value an overlay is responsible for. */
  overlaid?: Record<string, string>
  /** Set when a person took this row out of the return. */
  excluded_by?: { reason_code: string; note: string } | null
  /**
   * Set when a person ADDED this row — no extraction produced it. The screen
   * marks it, because a row with no document behind it must never be
   * indistinguishable from one read off a page.
   */
  added_by?: { reason_code: string; note: string } | null
  /** What the producer said, before any overlay. `effective` is what it says now. */
  extracted: Record<string, unknown>
  effective: Record<string, unknown>
  failing: string[]
  conflicts: Array<Record<string, unknown>>
  accepted: boolean
}

export interface ReviewSuggestion {
  content_key: string
  field: string
  value: unknown
  rule_id: string
  reason: string
  kind: string
  computed: boolean
}

export interface ReviewFlag {
  content_key: string
  field: string
  reason: string
}

export interface ReviewBody {
  document_id: string
  /**
   * ONE PAGE of rows. A real register is 40,568 lines and every one carries at
   * least a classification suggestion, so the unpaged read was 53 MB and the
   * screen rendered 7.7 million characters of it.
   */
  rows: ReviewRow[]
  /** The whole document's row count — never the page's. */
  total_rows: number
  limit: number
  offset: number
  /**
   * Whole-document tallies, deliberately NOT paged. `rows`, `suggestions` and
   * `flags` describe what is in front of you; these describe what there is, so
   * a reviewer looking at two hundred rows can still see that 19,899 need an
   * HSN. Never compute a total by counting the arrays above.
   */
  suggestion_counts: Record<string, number>
  flag_counts: Record<string, number>
  orphaned: Array<{ content_key: string; field: string }>
  suggestions: ReviewSuggestion[]
  flags: ReviewFlag[]
  /**
   * The fields a correction may touch — the SERVICE's list, served rather than
   * duplicated here. It used to be a byte-identical copy in this file, which is
   * one edit away from the two disagreeing about what may be changed.
   */
  correctable_fields: string[]
  /**
   * What a money correction may touch, in integer PAISE — served, never
   * duplicated here. Split from `rate_fields` so the form can show a rupee
   * placeholder without this file deciding which fields are money.
   */
  money_fields: string[]
  /** Correctable alongside money and deliberately NOT money: a percent, a count. */
  rate_fields: string[]
  /** code → label. The same dict the service validates against. */
  money_reasons: Record<string, string>
  /** What a reviewer may state on a row they add — served, never duplicated. */
  addable_fields: string[]
  /**
   * Whether this document is on the approval track, so a row added to it would
   * actually reach the return. The SERVICE answers; this file does not work it
   * out from a stage list of its own.
   */
  can_add_rows: boolean
}

export const review = {
  /**
   * Override the producer's arithmetic — several fields at once, because
   * fixing `taxable` alone leaves the row contradicting itself. Values go as
   * strings; the service coerces them to paise at a named boundary.
   */
  correctMoney: (
    id: string,
    body: {
      content_key: string
      values: Record<string, string>
      reason_code: string
      note: string
    },
  ) =>
    api<{ applied: Record<string, { old: unknown; new: number }>; demoted_periods: string[] }>(
      `/api/v1/review/${id}/money-corrections`,
      { method: 'POST', body },
    ),
  /** Take a row out of the return, or put it back. Append-only either way. */
  /**
   * Add a line the parser missed (Stage 4). The one call in this file that can
   * create money from nothing — which is why the client workbook discloses
   * every row it produces, and why the note is required and the client reads it.
   */
  addRow: (
    id: string,
    body: { line: Record<string, unknown>; reason_code: string; note: string },
  ) =>
    api<{ content_key: string; demoted_periods: string[] }>(
      `/api/v1/review/${id}/additions`,
      { method: 'POST', body },
    ),

  exclude: (
    id: string,
    body: { content_key: string; excluded: boolean; reason_code: string; note: string },
  ) =>
    api<{ excluded: boolean; demoted_periods: string[] }>(
      `/api/v1/review/${id}/exclusions`,
      { method: 'POST', body },
    ),
  /**
   * One person, one document: *I have looked at this and I stand behind it.*
   * The remark is required and whitespace is refused server-side — an empty
   * confirmation confirms nothing.
   */
  verify: (id: string, body: { remark: string }) =>
    api<{ verified: boolean; demoted_periods: string[] }>(
      `/api/v1/review/${id}/verify`,
      { method: 'POST', body },
    ),
  /**
   * The resolution queue as a workbook (X-09 Stage 3).
   *
   * Not through `api()`: that parses JSON and this response is a spreadsheet.
   * Headers are assembled the same way and `session.authed` still owns the
   * bearer and the refresh-and-replay.
   */
  hsnQueue: async (clientId: string, period?: string): Promise<Blob> => {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    const query = period ? `?period=${encodeURIComponent(period)}` : ''
    const res = await session.authed(
      `${SERVICE_API}/api/v1/review/hsn-queue/${clientId}${query}`,
      { method: 'GET', headers: { [ORG_HEADER]: currentOrgId } },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'The queue could not be built.',
        res.status,
      )
    }
    return res.blob()
  },
  /**
   * Send the filled sheet back. One bad code fails the whole file — the
   * service applies nothing rather than half of it, so a failure here means
   * the client's data is exactly as it was.
   */
  applyHsnQueue: (clientId: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<{
      products: number
      rows: number
      documents: number
      skipped_unchanged: number
      demoted_periods: string[]
    }>(`/api/v1/review/hsn-queue/${clientId}`, { method: 'POST', form })
  },
  get: (documentId: string, params: { limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ReviewBody>(`/api/v1/review/${documentId}${suffix}`)
  },
  correct: (documentId: string, body: { content_key: string; field: string; value: unknown }) =>
    api<{ applied: boolean }>(`/api/v1/review/${documentId}/corrections`, {
      method: 'POST',
      body,
    }),
  accept: (
    documentId: string,
    body: {
      content_key: string
      field: string
      value: unknown
      rule_id: string
      kind?: string
      computed?: boolean
      /** Teaches the UQC master, which is keyed by HSN (FR-22). */
      hsn?: string
      /** Teaches the HSN memory, which is keyed by the product (X-09). */
      item?: string
    },
  ) => api<{ accepted: boolean }>(`/api/v1/review/${documentId}/accept`, { method: 'POST', body }),
}

export interface ReconMatchOut {
  id: string
  status: string
  rung: string
  books_content_key: string
  tb_row_key: string
  tolerance: { name: string | null; value: Record<string, unknown> | null }
  evidence: Record<string, unknown> | null
}

export interface WorklistEntry {
  content_key?: string
  tb_row_key?: string
  supplier_gstin: string | null
  doc_no: string | null
  doc_date: string | null
  taxable: number | null
  age_days: number | null
  risk: string
  next_actions: string[]
  /** D-38: the month a books-only purchase was booked, when it is not this one. */
  carried_from?: string | null
}

export interface ReconState {
  import: { id: string; version_no: number; row_count: number; diff: Record<string, number> } | null
  suggested: ReconMatchOut[]
  stale: ReconMatchOut[]
  worklists: { books_only: WorklistEntry[]; tb_only: WorklistEntry[] }
  itc: {
    eligible: Record<string, number>
    held_stale: Record<string, number>
    disputed: Record<string, number>
    suppressed: Record<string, number>
    fallback: { state: string; gate: string }
  }
  books_notes: string[]
}

export const recon = {
  /** The portal's own 2B workbook — the file operators actually download. */
  importWorkbook: (input: { file: File; client_id: string; period: string }) => {
    const form = new FormData()
    form.set('file', input.file, input.file.name)
    form.set('client_id', input.client_id)
    form.set('period', input.period)
    return api<{ import_id: string; version_no: number; row_count: number }>(
      '/api/v1/recon/import-workbook',
      { method: 'POST', form },
    )
  },
  state: (clientId: string, period: string) =>
    api<ReconState>(`/api/v1/recon?client_id=${clientId}&period=${encodeURIComponent(period)}`),
  importRows: (body: {
    client_id: string
    period: string
    rows: Array<Record<string, unknown>>
    source_filename?: string
  }) => api<{ version_no: number; row_count: number }>('/api/v1/recon/import', { method: 'POST', body }),
  run: (body: { client_id: string; period: string }) =>
    api<{ matched: number; suggested: number }>('/api/v1/recon/run', { method: 'POST', body }),
  act: (body: {
    type: 'accept' | 'link' | 'suppress' | 'value_accept' | 'revert'
    client_id: string
    period: string
    match_id?: string
    action_id?: string
    books_content_key?: string
    tb_row_key?: string
    reason_code?: string
    heads?: Record<string, string>
    note?: string
  }) => api<{ action_id?: string; match_id?: string }>('/api/v1/recon/actions', { method: 'POST', body }),
}

export interface ReferenceValueOut {
  key: string
  value: Record<string, unknown> | null
  placeholder: boolean
  source_note: string
}

export const reference = {
  category: (category: string, period: string) =>
    api<{ values: ReferenceValueOut[] }>(
      `/api/v1/reference/${category}?period=${encodeURIComponent(period)}`,
    ),
}

export interface PeriodRecord {
  id: string
  client_id: string
  return_type: 'GSTR1' | 'GSTR3B'
  period_type: string
  period: string
  state: string
  artifact_sha256: string | null
  /** The acknowledgment that MARKED this filed. A person's word (FR-9). */
  filed_ack_no: string | null
  /** When the portal receipt CONFIRMED it. Null means marked, not confirmed. */
  filed_confirmed_at?: string | null
  /**
   * `prepared` — computed here. `snapshot` — a month filed before ProGST held
   * these books: stored, shown, never recomputed (ND-3).
   */
  origin?: 'prepared' | 'snapshot'
  history?: Array<{
    action: string
    detail: Record<string, unknown>
    actor_user_id: string | null
    at: string
  }>
}

export interface PeriodGates {
  period: PeriodRecord
  client_name: string
  blocks_prepared: boolean
  blocked_table12: Array<Record<string, unknown>>
  tieout: Array<Record<string, unknown>>
  pending: Array<Record<string, unknown>>
  excluded: Array<Record<string, unknown>>
  totals: Record<string, Record<string, number>>
  nil: Record<string, number>
  docs: Record<string, Record<string, unknown>>
  artifacts: Array<{
    id: string
    kind: string
    version_no: number
    status: string
    sha256: string
    generated_from_state: string
  }>
}

/**
 * The return's sections, row by row. Section keys the draft does not build
 * are simply absent — B2CL waits on V-01, exports on their V-rows, ECO on
 * V-11 — and the screen renders a tab only for what is here, which is how
 * the legacy behaves too.
 */
export interface PeriodTables {
  period: PeriodRecord
  tables: Record<string, unknown>
  totals: Record<string, { taxable?: number; igst?: number; cgst?: number; sgst?: number }>
  rule_stamp: Record<string, number>
}

export interface Gstr3bBody {
  period: PeriodRecord
  client_name: string
  client_gstin: string | null
  gstr3b: {
    period: string
    cells: Record<string, Record<string, unknown>>
    setoff?: {
      cash?: Record<string, number>
      utilisation?: Record<string, Record<string, number>>
      credit_remaining?: Record<string, number>
    }
    [k: string]: unknown
  }
  tieout: { ok: boolean; discrepancies: Array<Record<string, unknown>> }
}

export interface FiledHeads {
  taxable: number
  igst: number
  cgst: number
  sgst: number
  cess: number
}

export interface FiledComparisonBody {
  period: PeriodRecord
  client_name: string
  filed_gstin: string | null
  comparison: {
    convention: string
    ok: boolean
    sections: Array<{
      section: string
      prepared: FiledHeads
      filed: FiledHeads
      diff: FiledHeads
      ok: boolean
    }>
    mismatched: Array<Record<string, unknown>>
    prepared_only: Array<Record<string, unknown>>
    filed_only: Array<Record<string, unknown>>
  }
}

export interface TbReconBody {
  period: PeriodRecord
  client_name: string
  sheet: string
  reconciliation: {
    convention: string
    summary: {
      tb_taxable: number
      tb_exempt: number
      tb_non_gst: number
      gstr1_taxable: number
      gstr1_tax: number
      gap: number
      unclassified: number
    }
    income: Array<Record<string, unknown>>
    gst: Array<{ particulars: string; amount: number }>
    exempt: Array<Record<string, unknown>>
    unclassified: Array<Record<string, unknown>>
  }
}

export const periods = {
  list: (
    params: { client_id?: string; state?: string; filed_unconfirmed?: boolean } = {},
  ) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<PeriodRecord[]>(`/api/v1/periods${suffix}`)
  },
  /**
   * A month filed before ProGST held these books (X-05). The period is read
   * from the return's own `fp`, so there is nothing to type: the file says
   * which month it is.
   */
  importSnapshot: (body: { client_id: string; file: File; ack_no?: string }) => {
    const form = new FormData()
    form.set('client_id', body.client_id)
    form.set('file', body.file, body.file.name)
    if (body.ack_no) form.set('ack_no', body.ack_no)
    return api<PeriodRecord & { artifact_id: string; document_count: number }>(
      '/api/v1/periods/snapshots',
      { method: 'POST', form },
    )
  },
  get: (id: string) => api<PeriodRecord>(`/api/v1/periods/${id}`),
  open: (body: { client_id: string; return_type: string; period: string }) =>
    api<PeriodRecord>('/api/v1/periods', { method: 'POST', body }),
  gates: (id: string) => api<PeriodGates>(`/api/v1/periods/${id}/gates`),
  /** The section tabs' read — rows, not just totals. Fetched when a tab opens. */
  tables: (id: string) => api<PeriodTables>(`/api/v1/periods/${id}/tables`),
  gstr3b: (id: string) => api<Gstr3bBody>(`/api/v1/periods/${id}/gstr3b`),
  /** The books (TB sheet of the Main GST Data File) against the return. */
  tbReconciliation: (id: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<TbReconBody>(`/api/v1/periods/${id}/tb-reconciliation`, { method: 'POST', form })
  },
  /** Prepared vs the return as actually filed on the portal. */
  filedComparison: (id: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<FiledComparisonBody>(`/api/v1/periods/${id}/filed-comparison`, {
      method: 'POST',
      form,
    })
  },
  /**
   * Several artifacts as one ZIP. Packaging, not generation — every member
   * was produced at a lifecycle transition and carries its bound SHA.
   * An empty list means every current artifact.
   */
  artifactsZip: async (periodId: string, artifactIds: string[]): Promise<Blob> => {
    // Not through `api()`: that parses JSON, and this response is a ZIP.
    // The headers are assembled the same way, and `session.authed` still owns
    // the bearer and the refresh-and-replay.
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
    const res = await session.authed(
      `${SERVICE_API}/api/v1/periods/${periodId}/artifacts/zip`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ artifact_ids: artifactIds }),
      },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'The bundle could not be built.',
        res.status,
      )
    }
    return res.blob()
  },
  /** A freshly-minted read URL for one artifact. A capability, never stored. */
  artifactUrl: (periodId: string, artifactId: string) =>
    api<{ url: string; kind: string; version_no: number; sha256: string }>(
      `/api/v1/periods/${periodId}/artifacts/${artifactId}/download`,
    ),
  transition: (id: string, body: { action: string; artifact_sha256?: string; ack_no?: string; remark?: string }) =>
    api<PeriodRecord>(`/api/v1/periods/${id}/transition`, { method: 'POST', body }),
  sendForApproval: (id: string) =>
    api<PeriodRecord & { artifacts: Record<string, { sha256: string; version_no: number }> }>(
      `/api/v1/periods/${id}/send-for-approval`,
      { method: 'POST' },
    ),
}
